/**
 * Ordonnancement chronologique des arrêts — EF-CAL-009 étape 2,
 * EF-CAL-003, EF-CAL-004.
 *
 * L'application parcourt la chronologie à partir de la date et de l'heure de
 * départ en maintenant trois valeurs : l'horloge locale, le temps de roulage
 * déjà consommé et le compteur d'intervalle de pédalage.
 *
 * Aucune itération, aucun point fixe : l'heure d'arrivée n'a jamais besoin
 * d'être connue à l'avance. Ce parcours rend structurellement impossible qu'un
 * arrêt soit placé au-delà de l'arrivée.
 */

import {
  addSeconds,
  SECONDS_PER_DAY,
  startOfDay,
  type LocalDateTime,
} from '../core/local-datetime';
import type { ScheduleParameters } from '../core/parameters';
import type { Stop } from '../core/model';
import { progressionAtRidingTime, ridingTimeAt, type RidingTimeCurve } from './riding-time';

/** Seuil de déclenchement de la demande de sommeil, en secondes. */
export const SLEEP_PROMPT_THRESHOLD_SECONDS = 24 * 3600;

export interface ScheduleOptions {
  /**
   * Faux pour l'estimation intermédiaire de EF-CAL-004, qui sert uniquement à
   * décider s'il faut demander l'heure de coucher et la durée de sommeil.
   */
  readonly includeSleep: boolean;
  /** Instant d'origine. Par défaut, la date et l'heure de départ. */
  readonly startLocal?: LocalDateTime;
  /** Temps de roulage déjà consommé, pour un recalcul en mode course. */
  readonly consumedRidingSeconds?: number;
  /** Vrai pour émettre la ligne Départ. Faux lors d'un recalcul partiel. */
  readonly emitStartStop?: boolean;
}

/** Arrêt intermédiaire, avec le temps de roulage auquel il survient. */
interface StopEvent {
  readonly ridingSeconds: number;
  readonly durationSeconds: number;
  /** Temps d'arrêt cumulé, celui-ci compris. */
  readonly stoppedAfterSeconds: number;
}

export interface ScheduleResult {
  /** Lignes d'étape, Départ et Arrivée compris, par progression croissante. */
  readonly stops: readonly Stop[];
  readonly startLocal: LocalDateTime;
  readonly finishLocal: LocalDateTime;
  readonly ridingSeconds: number;
  readonly pauseSeconds: number;
  readonly sleepSeconds: number;
  readonly totalSeconds: number;
  /** Heure de passage estimée à une progression donnée. */
  readonly estimatedArrival: (progressionMeters: number) => LocalDateTime;
}

/**
 * Prochaine occurrence de l'heure de coucher, **strictement** postérieure à
 * l'instant donné. Lorsque l'heure de coucher coïncide avec l'heure de départ,
 * la première période de sommeil est donc celle du jour suivant.
 */
export function nextBedtime(after: LocalDateTime, bedTimeSecondsOfDay: number): LocalDateTime {
  let candidate = addSeconds(startOfDay(after), bedTimeSecondsOfDay);
  while (candidate <= after) candidate = addSeconds(candidate, SECONDS_PER_DAY);
  return candidate;
}

export function computeSchedule(
  curve: RidingTimeCurve,
  schedule: ScheduleParameters,
  options: ScheduleOptions,
): ScheduleResult {
  const startLocal = options.startLocal ?? schedule.startLocal;
  const consumedAtStart = options.consumedRidingSeconds ?? 0;
  const emitStart = options.emitStartStop ?? true;

  const usePauses = schedule.pauses.enabled;
  const useSleep = options.includeSleep && schedule.sleep.enabled;
  const pauseInterval = schedule.pauses.intervalSeconds;
  const pauseDuration = schedule.pauses.durationSeconds;
  const sleepDuration = schedule.sleep.durationSeconds;
  const bedTime = schedule.sleep.bedTimeSecondsOfDay;

  const stops: Stop[] = [];
  const events: StopEvent[] = [];

  if (emitStart) {
    stops.push({
      type: 'start',
      startLocal,
      endLocal: null,
      progressionMeters: progressionAtRidingTime(curve, consumedAtStart),
      durationSeconds: null,
      nightIndex: null,
    });
  }

  let clock = startLocal;
  let ridingConsumed = consumedAtStart;
  let pedalCounter = 0;
  let nightIndex = 0;
  let pauseSeconds = 0;
  let sleepSeconds = 0;
  let stoppedTotal = 0;

  const pushEvent = (ridingSeconds: number, durationSeconds: number): void => {
    stoppedTotal += durationSeconds;
    events.push({ ridingSeconds, durationSeconds, stoppedAfterSeconds: stoppedTotal });
  };

  for (;;) {
    const remainingRiding = Math.max(0, curve.totalSeconds - ridingConsumed);
    const arrivalAt = addSeconds(clock, remainingRiding);
    const pauseAt = usePauses
      ? addSeconds(clock, Math.max(0, pauseInterval - pedalCounter))
      : (Number.POSITIVE_INFINITY as unknown as LocalDateTime);
    const sleepAt = useSleep ? nextBedtime(clock, bedTime) : (Number.POSITIVE_INFINITY as unknown as LocalDateTime);

    // L'événement le plus précoce est retenu ; à égalité, l'arrivée l'emporte.
    if (arrivalAt <= pauseAt && arrivalAt <= sleepAt) {
      ridingConsumed = curve.totalSeconds;
      clock = arrivalAt;
      break;
    }

    if (sleepAt <= pauseAt) {
      const delta = sleepAt - clock;
      ridingConsumed += delta;
      pedalCounter += delta;
      const progression = progressionAtRidingTime(curve, ridingConsumed);
      nightIndex += 1;
      stops.push({
        type: 'sleep',
        startLocal: sleepAt,
        endLocal: addSeconds(sleepAt, sleepDuration),
        progressionMeters: progression,
        durationSeconds: sleepDuration,
        nightIndex,
      });
      pushEvent(ridingConsumed, sleepDuration);
      sleepSeconds += sleepDuration;
      clock = addSeconds(sleepAt, sleepDuration);
      // Une période de sommeil vaut repos : le compteur repart à zéro.
      pedalCounter = 0;
      continue;
    }

    const delta = pauseAt - clock;
    ridingConsumed += delta;
    pedalCounter += delta;
    const progression = progressionAtRidingTime(curve, ridingConsumed);

    // Une pause commencée avant l'heure de coucher et qui déborderait sur la
    // période de sommeil est tronquée à l'heure de coucher ; la durée retirée
    // n'est pas reportée.
    let duration = pauseDuration;
    let truncated = false;
    if (useSleep && pauseAt + pauseDuration > sleepAt) {
      duration = sleepAt - pauseAt;
      truncated = true;
    }

    stops.push({
      type: 'pause',
      startLocal: pauseAt,
      endLocal: addSeconds(pauseAt, duration),
      progressionMeters: progression,
      durationSeconds: duration,
      nightIndex: null,
    });
    pushEvent(ridingConsumed, duration);
    pauseSeconds += duration;
    clock = addSeconds(pauseAt, duration);
    pedalCounter = 0;

    if (truncated) {
      // Le sommeil enchaîne immédiatement, à la même progression.
      nightIndex += 1;
      stops.push({
        type: 'sleep',
        startLocal: clock,
        endLocal: addSeconds(clock, sleepDuration),
        progressionMeters: progression,
        durationSeconds: sleepDuration,
        nightIndex,
      });
      pushEvent(ridingConsumed, sleepDuration);
      sleepSeconds += sleepDuration;
      clock = addSeconds(clock, sleepDuration);
      pedalCounter = 0;
    }
  }

  const finishLocal = clock;
  stops.push({
    type: 'finish',
    startLocal: finishLocal,
    endLocal: null,
    progressionMeters: curve.lengthMeters,
    durationSeconds: null,
    nightIndex: null,
  });

  const ridingSeconds = Math.max(0, curve.totalSeconds - consumedAtStart);

  return {
    stops,
    startLocal,
    finishLocal,
    ridingSeconds,
    pauseSeconds,
    sleepSeconds,
    totalSeconds: finishLocal - startLocal,
    estimatedArrival: (progressionMeters: number): LocalDateTime => {
      const riding = ridingTimeAt(curve, progressionMeters);
      const elapsedRiding = Math.max(0, riding - consumedAtStart);
      return addSeconds(startLocal, elapsedRiding + stoppedBefore(events, riding));
    },
  };
}

/**
 * Temps d'arrêt cumulé avant d'atteindre un temps de roulage donné.
 *
 * La comparaison est stricte : à progression égale, une ligne de POI précède
 * une Pause et un Sommeil selon l'ordre total de EF-CAL-006, donc l'arrêt ne
 * décale pas l'heure de passage du POI.
 */
function stoppedBefore(events: readonly StopEvent[], ridingSeconds: number): number {
  let low = 0;
  let high = events.length - 1;
  let found = -1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const event = events[middle];
    if (event === undefined) break;
    if (event.ridingSeconds < ridingSeconds) {
      found = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  if (found < 0) return 0;
  return events[found]?.stoppedAfterSeconds ?? 0;
}

/**
 * Estimation intermédiaire de EF-CAL-004 : exécution de l'étape 2 avec les
 * pauses et sans sommeil. Elle n'est jamais affichée comme résultat et sert
 * uniquement à décider s'il faut demander l'heure de coucher.
 */
export function requiresSleepPrompt(curve: RidingTimeCurve, schedule: ScheduleParameters): boolean {
  const intermediate = computeSchedule(curve, schedule, { includeSleep: false });
  return intermediate.totalSeconds > SLEEP_PROMPT_THRESHOLD_SECONDS;
}
